import { Box } from '@mui/material'
import { useMemo } from 'react'
import { formatToK, transformPieChartData } from '../utils/transformPieChartData'
import { DataPieChart, PieChartDataItem } from '../components/DataPieChart'
import { ChartCard } from '../components/ChartCard'
import { useDataSentryContext } from '../context/DataSentryContext'
import { useDataSentryBySystemsServiceType } from 'api/dataSentry/queries/useDataSentryBySystemsServiceType'
import { transformSystemsByServiceType } from '../utils/transformSystemsByServiceTypes'
import { DataSentrySystemsTable } from './systemsTabComponents/DataSentrySystemsTable'
import { TopDataCollectingSystems } from './systemsTabComponents/TopDataCollectingSystems'
import { TopUnAuthorizedSystemsCollectingData } from './systemsTabComponents/TopUnAuthorizedSystemsCollectingData'
import { useDataSentryEventsByClassification } from 'api/dataSentry/queries/useDataSentryEventsByClassification'
import {
  ClassificationType,
  AuthorizationStatus as EventsAuthorizationStatus,
} from '@ketch-com/windlass/dist/dataSentry/v1/events/events'
import { AuthorizationStatus as SystemsAuthorizationStatus } from '@ketch-com/windlass/dist/dataSentry/v1/systems/systems'
import { Card, Icon, theme, IconDictionary } from '@ketch-com/deck'
import pluralize from 'pluralize'
import { ConsentControl } from 'pages/dataSentry/systems/systemsTabComponents/ConsentControl'

export interface GenericPieChartProps {
  data: PieChartDataItem[]
  isLoading: boolean
}

export const GenericPieChart: React.FC<GenericPieChartProps> = ({ data, isLoading }) => {
  const chartData = isLoading || !data || data.length === 0 ? [] : data
  return <DataPieChart data={chartData} />
}

export const SystemsTab = () => {
  const {
    selectedTimeRange,
    eventsSummary,
    systemsSummary,
    selectedProperty,
    isTopSystemsLoading,
    topSystems,
    selectedPropertyCode,
    topUnauthorizedSystems,
    isTopUnauthorizedSystemsLoading,
  } = useDataSentryContext()

  const { data: systemsServiceData, isLoading: isSystemsServiceLoading } = useDataSentryBySystemsServiceType({
    params: {
      'start-date': selectedTimeRange?.startDate,
      'end-date': selectedTimeRange?.endDate,
      'property-codes': selectedProperty?.code,
    },
  })

  const { data: categoryData, isLoading: isCategoryDataLoading } = useDataSentryEventsByClassification({
    params: {
      'classification-type': ClassificationType.DATA_CATEGORY,
      'start-date': selectedTimeRange?.startDate,
      'end-date': selectedTimeRange?.endDate,
      'property-codes': selectedPropertyCode,
    },
  })

  const transformedSystemTypesData = useMemo(() => {
    if (!systemsServiceData || !('serviceTypes' in systemsServiceData)) {
      return []
    }

    if (!systemsServiceData.serviceTypes?.length) {
      return []
    }

    return transformSystemsByServiceType(systemsServiceData.serviceTypes)
  }, [systemsServiceData])
  const transformedDataCategoriesData = useMemo(
    () => transformPieChartData(categoryData?.classifications || []),
    [categoryData],
  )

  const unauthorizedSystems = systemsSummary?.authorizationTypes?.find(
    system => system.status === SystemsAuthorizationStatus.UNAUTHORIZED,
  )
  const unauthorizedCollectionEvents = eventsSummary?.authorizationTypes?.find(
    event => event.status === EventsAuthorizationStatus.UNAUTHORIZED,
  )
  const unauthorizedSystemsCount = unauthorizedSystems?.count || 0
  const unauthorizedCollectionEventsCount = unauthorizedCollectionEvents?.count || 0

  return (
    <Box width="100%" display="flex" flexDirection="column" gap={6} pb={16} mt={3}>
      <Box display="flex" gap={3}>
        <ChartCard title="System Types" isLoading={isSystemsServiceLoading}>
          <GenericPieChart data={transformedSystemTypesData} isLoading={isSystemsServiceLoading} />
        </ChartCard>
        <Box display="flex" flexWrap="wrap" flexBasis={402} gap={2}>
          <Box flexBasis={193} display="flex" flexDirection="column">
            <Card
              cardVariant="stretchWidth"
              cardTitle={formatToK(systemsSummary?.total || 0)}
              subTitle={pluralize('System', systemsSummary?.total)}
              primaryIcon={
                <Icon name={IconDictionary.System} width="32px" height="32px" iconColor={theme.palette.Black.o48} />
              }
              isNumerical
              backgroundColor={systemsSummary?.total ? theme.palette.White.o100 : 'transparent'}
              textColor={systemsSummary?.total ? theme.palette.Text.Primary : theme.palette.Text.Secondary}
              sx={{
                border: systemsSummary?.total ? 'none' : `1px solid ${theme.palette.Black.o8}`,
                padding: 2,
              }}
            />
          </Box>
          <Box flexBasis={193} display="flex" flexDirection="column">
            <Card
              cardVariant="stretchWidth"
              cardTitle={formatToK(eventsSummary?.total || 0)}
              subTitle={pluralize('Collection Event', eventsSummary?.total)}
              primaryIcon={
                <Icon
                  name={IconDictionary.OTransformDown}
                  width="32px"
                  height="32px"
                  iconColor={theme.palette.Black.o48}
                />
              }
              isNumerical
              backgroundColor={eventsSummary?.total ? theme.palette.White.o100 : 'transparent'}
              textColor={eventsSummary?.total ? theme.palette.Text.Primary : theme.palette.Text.Secondary}
              sx={{
                border: eventsSummary?.total ? 'none' : `1px solid ${theme.palette.Black.o8}`,
                padding: 2,
              }}
            />
          </Box>
          <Box flexBasis={193} display="flex" flexDirection="column">
            <Card
              cardVariant="stretchWidth"
              cardTitle={formatToK(unauthorizedSystemsCount || 0)}
              subTitle={pluralize('Unauthorized System', unauthorizedSystemsCount)}
              primaryIcon={
                <Icon
                  name={IconDictionary.OSystemUnknown}
                  width="32px"
                  height="32px"
                  iconColor={theme.palette.Black.o48}
                />
              }
              isNumerical
              backgroundColor={unauthorizedSystemsCount ? theme.palette.Black.o8 : 'transparent'}
              textColor={unauthorizedSystemsCount ? theme.palette.Text.Primary : theme.palette.Text.Secondary}
              sx={{
                border: systemsSummary?.total ? 'none' : `1px solid ${theme.palette.Black.o8}`,
                padding: 2,
              }}
            />
          </Box>
          <Box flexBasis={193} display="flex" flexDirection="column">
            <Card
              cardVariant="stretchWidth"
              cardTitle={formatToK(unauthorizedCollectionEventsCount || 0)}
              subTitle={pluralize('Unauthorized Collection Event', unauthorizedCollectionEventsCount)}
              primaryIcon={
                <Icon
                  name={IconDictionary.FTransformDown}
                  width="32px"
                  height="32px"
                  iconColor={theme.palette.Black.o48}
                />
              }
              isNumerical
              backgroundColor={unauthorizedCollectionEventsCount ? theme.palette.Black.o8 : 'transparent'}
              textColor={unauthorizedCollectionEventsCount ? theme.palette.Text.Primary : theme.palette.Text.Secondary}
              sx={{
                border: eventsSummary?.total ? 'none' : `1px solid ${theme.palette.Black.o8}`,
                padding: 2,
              }}
            />
          </Box>
        </Box>
        <ChartCard title="Data Categories Collected" isLoading={isCategoryDataLoading}>
          <GenericPieChart data={transformedDataCategoriesData} isLoading={isCategoryDataLoading} />
        </ChartCard>
      </Box>
      <Box display="flex" gap={3}>
        <TopDataCollectingSystems systems={topSystems} isLoading={!!isTopSystemsLoading} />
        <TopUnAuthorizedSystemsCollectingData
          systems={topUnauthorizedSystems}
          isLoading={!!isTopUnauthorizedSystemsLoading}
        />
      </Box>

      <Box display="flex" gap={3} minHeight={480}>
        <ConsentControl />
        <DataSentrySystemsTable />
      </Box>
    </Box>
  )
}
